const parseSystemDataValue = (systemData) => {
  if (!systemData) {
    return null;
  }

  if (typeof systemData === "string") {
    try {
      return JSON.parse(systemData);
    } catch (error) {
      console.error("Failed to parse systemData payload", error);
      return null;
    }
  }

  if (typeof systemData === "object") {
    return systemData;
  }

  return null;
};

const parseJsonObject = (value) => {
  if (typeof value === "string") {
    try {
      const parsedValue = JSON.parse(value);
      return parsedValue && typeof parsedValue === "object"
        ? parsedValue
        : null;
    } catch {
      return null;
    }
  }

  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : null;
};

const getEmbeddedSystemData = (systemData) => {
  const parsedSystemData = parseSystemDataValue(systemData);
  if (!parsedSystemData) {
    return null;
  }

  return parseJsonObject(parsedSystemData.json) || parsedSystemData;
};

const objectValues = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  return value && typeof value === "object" ? Object.values(value) : [];
};

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object, key);

const readString = (object, keys) => {
  for (const key of keys) {
    const value = object?.[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
    if (typeof value === "number") {
      return String(value);
    }
  }

  return "";
};

export const getSystemEnvironments = (systemData) => {
  const embeddedSystemData = getEmbeddedSystemData(systemData);
  if (!embeddedSystemData) {
    return [];
  }

  if (
    embeddedSystemData.environments &&
    typeof embeddedSystemData.environments === "object"
  ) {
    return Object.values(embeddedSystemData.environments);
  }

  return [];
};

const normalizeId = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
};

const getElementIdCandidates = (elementData) => {
  const candidates = [
    elementData?.id,
    elementData?.elementID,
    elementData?.elementId,
    elementData?.elementData?.id,
  ];

  return candidates
    .map(normalizeId)
    .filter(Boolean);
};

const getEnvironmentElementIdCandidates = (environmentElement) => {
  const candidates = [
    environmentElement?.elementID,
    environmentElement?.elementId,
    environmentElement?.id,
    environmentElement?.elementData?.id,
  ];

  return candidates
    .map(normalizeId)
    .filter(Boolean);
};

const getTargetEnvironmentId = (environmentElement) => {
  const targetEnvironmentId =
    environmentElement?.targetEnvironmentId ||
    environmentElement?.targetEnvironmentID ||
    environmentElement?.targetEnvironment?.id ||
    environmentElement?.targetEnvironmentData?.id ||
    environmentElement?.targetEnvironmentDataID ||
    "";

  return normalizeId(targetEnvironmentId);
};

const getEnvironmentIdCandidates = (environment) =>
  [environment?.id, environment?.environmentId, environment?.environmentID]
    .map(normalizeId)
    .filter(Boolean);

export const getSystemCurrentEnvironmentId = (systemData) => {
  const embeddedSystemData = getEmbeddedSystemData(systemData);
  if (!embeddedSystemData) {
    return "";
  }

  return normalizeId(
    embeddedSystemData.currentEnvironmentId ||
      embeddedSystemData.currentEnvironmentID,
  );
};

const getWebMapNodes = (systemData) => {
  const parsedSystemData = parseSystemDataValue(systemData);
  const embeddedSystemData = getEmbeddedSystemData(systemData);
  const webMap = parseJsonObject(
    parsedSystemData?.webMap || embeddedSystemData?.webMap,
  );
  const sourceNodes =
    webMap?.nodes ||
    webMap?.environments ||
    (webMap && !hasOwn(webMap, "version") ? webMap : null);

  if (Array.isArray(sourceNodes)) {
    return sourceNodes.reduce((nodes, node) => {
      const environmentId = readString(node, [
        "id",
        "environmentId",
        "environmentID",
      ]);
      if (environmentId) {
        nodes[environmentId] = node;
      }

      return nodes;
    }, {});
  }

  return sourceNodes && typeof sourceNodes === "object" ? sourceNodes : {};
};

const getConditionKeys = (condition) =>
  Array.from(
    new Set(
      [
        readString(condition, [
          "id",
          "conditionId",
          "conditionID",
          "conditionDataId",
          "conditionDataID",
        ]),
        readString(condition, ["name", "conditionName"]),
      ].filter(Boolean),
    ),
  );

const readConditionValue = (value) => {
  if (
    typeof value === "boolean" ||
    typeof value === "number" ||
    typeof value === "string"
  ) {
    return value;
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  for (const key of [
    "isMet",
    "met",
    "satisfied",
    "active",
    "enabled",
    "value",
    "currentValue",
    "boolValue",
    "booleanValue",
    "stringValue",
    "numberValue",
    "value1",
  ]) {
    if (hasOwn(value, key)) {
      const nestedValue = value[key];
      if (
        typeof nestedValue === "boolean" ||
        typeof nestedValue === "number" ||
        typeof nestedValue === "string"
      ) {
        return nestedValue;
      }
    }
  }

  return null;
};

const isConditionValueMet = (value) => {
  const conditionValue = readConditionValue(value);
  if (typeof conditionValue === "boolean") {
    return conditionValue;
  }
  if (typeof conditionValue === "number") {
    return conditionValue !== 0;
  }
  if (typeof conditionValue === "string") {
    const normalizedValue = conditionValue.trim().toLowerCase();
    return (
      normalizedValue !== "" &&
      normalizedValue !== "0" &&
      normalizedValue !== "false" &&
      normalizedValue !== "no" &&
      normalizedValue !== "off" &&
      normalizedValue !== "unmet" &&
      normalizedValue !== "not met" &&
      normalizedValue !== "not_met"
    );
  }

  return false;
};

const parseConditionScalar = (value) => {
  const trimmedValue = value.trim();
  const normalizedValue = trimmedValue.toLowerCase();
  if (normalizedValue === "true") {
    return true;
  }
  if (normalizedValue === "false") {
    return false;
  }

  const numericValue = Number(trimmedValue);
  if (trimmedValue && Number.isFinite(numericValue)) {
    return numericValue;
  }

  return trimmedValue;
};

const parseConditionExpression = (value) => {
  const trimmedValue = value.trim();
  if (
    !trimmedValue ||
    trimmedValue.startsWith("{") ||
    trimmedValue.startsWith("[")
  ) {
    return null;
  }

  const match = trimmedValue.match(/^(.+?)\s*[,=:]\s*(.+)$/);
  if (!match) {
    return null;
  }

  const key = match[1].trim();
  const rawValue = match[2].trim().replace(/^['"]|['"]$/g, "");
  if (!key || !rawValue) {
    return null;
  }

  return {
    key,
    value: parseConditionScalar(rawValue),
  };
};

const conditionValuesMatch = (actual, expected) => {
  const actualValue = readConditionValue(actual);
  const expectedValue = readConditionValue(expected);

  if (actualValue === null || expectedValue === null) {
    return false;
  }

  if (typeof expectedValue === "boolean") {
    return isConditionValueMet(actualValue) === expectedValue;
  }

  if (typeof actualValue === "boolean") {
    return actualValue === isConditionValueMet(expectedValue);
  }

  if (typeof expectedValue === "number") {
    const actualNumber =
      typeof actualValue === "number" ? actualValue : Number(actualValue);
    return Number.isFinite(actualNumber) && actualNumber === expectedValue;
  }

  return String(actualValue).trim() === String(expectedValue).trim();
};

const addConditionExpression = (conditionValues, value) => {
  const expression = parseConditionExpression(value);
  if (!expression) {
    return false;
  }

  conditionValues[expression.key] = expression.value;
  return true;
};

const addConditionValue = (conditionValues, key, value) => {
  const conditionValue = readConditionValue(value);
  if (key && conditionValue !== null) {
    conditionValues[key] = conditionValue;
  }
};

const collectConditionValues = (source) => {
  const conditionValues = {};

  const collect = (value, keyedName = "") => {
    if (Array.isArray(value)) {
      value.forEach((item) => collect(item));
      return;
    }

    if (typeof value === "string") {
      const parsedValue = parseJsonObject(value);
      if (parsedValue) {
        collect(parsedValue, keyedName);
      } else if (!addConditionExpression(conditionValues, value)) {
        addConditionValue(conditionValues, keyedName, value);
      }
      return;
    }

    if (!value || typeof value !== "object") {
      addConditionValue(conditionValues, keyedName, value);
      return;
    }

    const nestedConditions = [
      value.conditions,
      value.conditionDatas,
      value.conditionData,
      value.condition,
    ].filter((nestedValue) => nestedValue !== undefined);
    const directValue = readConditionValue(value);

    if (nestedConditions.length > 0 && directValue === null) {
      nestedConditions.forEach((nestedValue) => collect(nestedValue));
      return;
    }

    const keys = getConditionKeys(value);
    if (keyedName) {
      addConditionValue(conditionValues, keyedName, value);
    }
    keys.forEach((key) => addConditionValue(conditionValues, key, value));

    if (keys.length === 0 && keyedName) {
      addConditionValue(conditionValues, keyedName, value);
      return;
    }

    if (keys.length > 0) {
      return;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
      collect(nestedValue, key);
    });
  };

  collect(source);
  return conditionValues;
};

const getSystemConditionValues = (systemData) => ({
  ...collectConditionValues(systemData?.conditions),
  ...collectConditionValues(systemData?.conditionDatas),
});

const getEnvironmentVisibilityConditions = (environment, webMapNode) => [
  ...objectValues(environment?.visibilityConditions),
  ...objectValues(environment?.visibilityConditionDatas),
  ...objectValues(environment?.environmentBubbleVisibilityConditions),
  ...objectValues(webMapNode?.visibilityConditions),
  ...objectValues(webMapNode?.visibilityConditionDatas),
];

const isVisibilityConditionMet = (condition, conditionValues) => {
  if (typeof condition === "string") {
    const expression = parseConditionExpression(condition);
    if (expression && hasOwn(conditionValues, expression.key)) {
      return conditionValuesMatch(
        conditionValues[expression.key],
        expression.value,
      );
    }
  }

  if (!condition || typeof condition !== "object" || Array.isArray(condition)) {
    return isConditionValueMet(condition);
  }

  const keys = getConditionKeys(condition);
  const expectedValue = readConditionValue(condition);
  for (const key of keys) {
    if (hasOwn(conditionValues, key)) {
      return expectedValue === null
        ? isConditionValueMet(conditionValues[key])
        : conditionValuesMatch(conditionValues[key], expectedValue);
    }
  }

  return expectedValue !== null ? isConditionValueMet(expectedValue) : false;
};

const isEnvironmentBubbleVisible = (environment, conditionValues, webMapNode) => {
  const visibilityConditions = getEnvironmentVisibilityConditions(
    environment,
    webMapNode,
  );

  return (
    visibilityConditions.length === 0 ||
    visibilityConditions.every((condition) =>
      isVisibilityConditionMet(condition, conditionValues),
    )
  );
};

export const getVisibleSystemEnvironments = (
  systemData,
  conditionValues = {},
) => {
  const environmentAvailabilityById = getSystemEnvironmentAvailabilityById(
    systemData,
    conditionValues,
  );
  return getSystemEnvironments(systemData).filter((environment) => {
    const environmentId = getEnvironmentIdCandidates(environment)[0];
    return environmentAvailabilityById[environmentId] !== false;
  });
};

export const getSystemEnvironmentAvailabilityById = (
  systemData,
  conditionValues = {},
) => {
  const parsedSystemData = parseSystemDataValue(systemData);
  const embeddedSystemData = getEmbeddedSystemData(systemData);
  const environments = getSystemEnvironments(systemData);
  const webMapNodes = getWebMapNodes(systemData);
  const effectiveConditionValues = {
    ...getSystemConditionValues(parsedSystemData),
    ...getSystemConditionValues(embeddedSystemData),
    ...(conditionValues || {}),
  };

  return environments.reduce((availabilityById, environment) => {
    const environmentId = getEnvironmentIdCandidates(environment)[0];
    if (!environmentId) {
      return availabilityById;
    }

    availabilityById[environmentId] = isEnvironmentBubbleVisible(
      environment,
      effectiveConditionValues,
      webMapNodes[environmentId],
    );
    return availabilityById;
  }, {});
};

export const parseConditionChangedEvent = (payload) => {
  const conditionValues = collectConditionValues(payload);
  return Object.keys(conditionValues).length > 0 ? conditionValues : null;
};

const getEnvironmentElements = (environment) => {
  const sources = [
    environment?.elements,
    environment?.elementJsons,
    environment?.elementDatas,
  ];

  for (const source of sources) {
    if (Array.isArray(source) && source.length > 0) {
      return source;
    }

    if (source && typeof source === "object") {
      const values = Object.values(source);
      if (values.length > 0) {
        return values;
      }
    }
  }

  return [];
};

const getDirectParentEnvironmentId = (environment) =>
  normalizeId(
    environment?.parentEnvironmentId ||
      environment?.parentEnvironmentID ||
      environment?.parentEnvironment?.id ||
      environment?.parentEnvironmentData?.id ||
      environment?.parentEnvironmentDataID ||
      environment?.parentId ||
      environment?.parentID ||
      environment?.parent?.id,
  );

export const getSelectedElementTargetEnvironmentId = ({
  systemData,
  currentEnvironmentId,
  elementData,
}) => {
  const selectedElementIds = getElementIdCandidates(elementData);
  if (!selectedElementIds.length) {
    return "";
  }

  const selectedElementIdSet = new Set(selectedElementIds);
  const environments = getSystemEnvironments(systemData);
  const validEnvironmentIds = new Set(
    environments.flatMap((environment) => getEnvironmentIdCandidates(environment)),
  );
  const normalizedCurrentEnvironmentId = normalizeId(currentEnvironmentId);
  const currentEnvironment = environments.find(
    (environment) => {
      const environmentIds = getEnvironmentIdCandidates(environment);
      return environmentIds.includes(normalizedCurrentEnvironmentId);
    },
  );
  const candidateEnvironments = currentEnvironment
    ? [currentEnvironment]
    : environments;

  for (const environment of candidateEnvironments) {
    for (const environmentElement of getEnvironmentElements(environment)) {
      const environmentElementIds =
        getEnvironmentElementIdCandidates(environmentElement);
      const matchesSelectedElement = environmentElementIds.some((id) =>
        selectedElementIdSet.has(id),
      );

      if (matchesSelectedElement) {
        const targetEnvironmentId = getTargetEnvironmentId(environmentElement);
        return validEnvironmentIds.has(targetEnvironmentId)
          ? targetEnvironmentId
          : "";
      }
    }
  }

  return "";
};

export const getParentEnvironmentId = ({
  systemData,
  currentEnvironmentId,
  currentEnvironment,
}) => {
  const normalizedCurrentEnvironmentId = normalizeId(
    currentEnvironmentId || currentEnvironment?.id,
  );
  if (!normalizedCurrentEnvironmentId) {
    return "";
  }

  const environments = getSystemEnvironments(systemData);
  const validEnvironmentIds = new Set(
    environments.flatMap((environment) => getEnvironmentIdCandidates(environment)),
  );
  const matchingSystemEnvironment = environments.find((environment) =>
    getEnvironmentIdCandidates(environment).includes(normalizedCurrentEnvironmentId),
  );
  const directParentEnvironmentId =
    getDirectParentEnvironmentId(currentEnvironment) ||
    getDirectParentEnvironmentId(matchingSystemEnvironment);

  if (
    directParentEnvironmentId &&
    (validEnvironmentIds.size === 0 ||
      validEnvironmentIds.has(directParentEnvironmentId))
  ) {
    return directParentEnvironmentId;
  }

  for (const environment of environments) {
    const sourceEnvironmentId = getEnvironmentIdCandidates(environment).find(Boolean);
    if (!sourceEnvironmentId) {
      continue;
    }

    const hasCurrentEnvironmentTarget = getEnvironmentElements(environment).some(
      (environmentElement) =>
        getTargetEnvironmentId(environmentElement) === normalizedCurrentEnvironmentId,
    );

    if (hasCurrentEnvironmentTarget) {
      return sourceEnvironmentId;
    }
  }

  return "";
};

export const normalizeEnvironmentLoadStatesPayload = (payload) => {
  if (!payload) {
    return null;
  }

  if (typeof payload === "string") {
    const trimmedPayload = payload.trim();
    if (!trimmedPayload) {
      return null;
    }

    try {
      return normalizeEnvironmentLoadStatesPayload(JSON.parse(trimmedPayload));
    } catch (error) {
      console.error("Failed to parse environmentLoadStatesChanged payload", error);
      return null;
    }
  }

  if (typeof payload !== "object") {
    return null;
  }

  const environments = Array.isArray(payload.environments)
    ? payload.environments
    : [];

  return {
    ...payload,
    currentEnvironmentId: normalizeId(payload.currentEnvironmentId),
    environments: environments.filter(
      (environment) => environment && typeof environment === "object",
    ),
  };
};

export const getEnvironmentLoadStateById = (
  environmentLoadStatesPayload,
  environmentId,
) => {
  const normalizedEnvironmentId = normalizeId(environmentId);
  if (!normalizedEnvironmentId) {
    return null;
  }

  const normalizedPayload = normalizeEnvironmentLoadStatesPayload(
    environmentLoadStatesPayload,
  );
  if (!normalizedPayload) {
    return null;
  }

  return (
    normalizedPayload.environments.find(
      (environment) => normalizeId(environment.id) === normalizedEnvironmentId,
    ) || null
  );
};

export const canEnterEnvironment = (
  environmentLoadStatesPayload,
  environmentId,
) =>
  getEnvironmentLoadStateById(environmentLoadStatesPayload, environmentId)
    ?.canEnter === true;
